/**
 * @file useThemeDraft.ts
 * @description Документ оформления в черновике с автосохранением
 * (phase19.16 Р1; контракт §1 `PATCH /draft/theme` — документ целиком).
 *
 * Состояние живёт в каркасе вида сайта (`useSiteViewState` →
 * `useSiteDraftParts`), а не в странице: «Оформление», «Шапка и подвал» и
 * предпросмотр читают один документ, правки не теряются при переходе между
 * панелями, а будущая оболочка «Сайт → Конструктор» (фаза §7.14) держит один
 * iframe на все панели. Конфликт ловит версия части THEME.
 */

import type { SiteTheme, SiteThemeSection } from "@chaiberi/shared";
import { useCallback } from "react";

import { useAutosavePart, type AutosaveApi } from "./useAutosavePart";
import { siteView } from "../../lib/api-site-view";
import type { SitePartState, SiteViewDraft } from "../../lib/site-view/types";

export interface ThemeDraftApi extends AutosaveApi<SiteTheme> {
  theme: SiteTheme;
  /** Правка раздела документа: `update("colors", { button: "#…" })`. */
  update: <S extends SiteThemeSection>(section: S, patch: Partial<SiteTheme[S]>) => void;
  /** Заменить документ целиком («Начать с вида по умолчанию»). */
  replace: (theme: SiteTheme) => void;
}

/** Сообщить каркасу новую версию части и значение после сохранения. */
export type ApplyPart = (
  part: SitePartState,
  patch?: Partial<Pick<SiteViewDraft, "theme" | "brand" | "seo" | "blog" | "catalog" | "counters" | "consentPlatform" | "navigation">>,
) => void;

/** Состояние оформления; `null` — черновик ещё не загружен. */
export function useThemeDraftState(draft: SiteViewDraft | null, applyPart: ApplyPart): ThemeDraftApi | null {
  const api = useAutosavePart<SiteTheme>({
    serverValue: draft?.theme ?? null,
    serverVersion: draft?.parts.THEME.version ?? 0,
    save: async (theme, expectedVersion) => {
      const res = await siteView.saveTheme(expectedVersion, theme);
      return { part: res.part, value: res.theme };
    },
    load: async () => {
      const res = await siteView.theme();
      return { part: res.part, value: res.theme };
    },
    onSaved: (result) => applyPart(result.part, { theme: result.value }),
  });
  const theme = api.value ?? draft?.theme ?? null;
  const { change } = api;

  const update = useCallback(
    <S extends SiteThemeSection>(section: S, patch: Partial<SiteTheme[S]>) => {
      if (theme) change({ ...theme, [section]: { ...theme[section], ...patch } }, [section]);
    },
    [change, theme],
  );
  const replace = useCallback((next: SiteTheme) => change(next, ["colors"]), [change]);

  return theme ? { ...api, theme, update, replace } : null;
}
